import { ReactNode, useState, useRef, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import {
  LayoutDashboard, FolderKanban, Users, Building2,
  CalendarDays, Monitor, KeyRound, Server,
  Bell, Menu as MenuIcon, X, Settings, Brain, ShieldCheck,
  ChevronDown, FileCheck, UserMinus, ClipboardCheck,
  Inbox, AlertTriangle, LogOut, Clock, Coffee, ChartPie, UserCheck,
  CalendarX,
  CalendarCheck,
  UserX,
  ScanEye,
  UserPlus,
  Github,
  ListChecks,
  BookCheck,
  FileSpreadsheet,
  ListTodo,
  ClipboardList,
  UserCog,
  UsersRound,
  Activity,
} from 'lucide-react'
import { supabase } from "@/lib/supabase"
import { useAuth } from '@/hooks/use-auth'
import { useTabAccess } from '@/hooks/use-tab-access'
import Footer from './footer'

import type { LucideIcon } from 'lucide-react'

interface NavItem {
  name: string
  href: string
  icon: LucideIcon
}

interface NavDropdown {
  name: string
  icon: LucideIcon
  children: NavItem[]
}

type NavEntry = NavItem | NavDropdown

function isDropdown(entry: NavEntry): entry is NavDropdown {
  return 'children' in entry
}

// Exported so Settings -> Access derives its page labels and page order from the
// menu staff already see, instead of keeping a second copy that drifts.
export const navigationItems: NavEntry[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  {
    name: 'HR / Admin',
    icon: ShieldCheck,
    children: [
      { name: 'SSM', href: '/ssm', icon: Monitor },
      { name: 'SSM Live Monitoring', href: '/ssm-live-monitoring', icon: Monitor },
      { name: 'Late Login Report', href: '/late-login-report', icon: AlertTriangle },
      { name: 'Early Logout Report', href: '/early-logout-report', icon: LogOut },
      { name: 'Active Hours Report', href: '/active-hours-report', icon: Clock },
      { name: 'Last-Hour Activity', href: '/breaktime-analysis', icon: Coffee },
      { name: 'Leave Penalty Report', href: '/leave-penalty-report', icon: CalendarX },
      { name: 'Absent Report', href: '/absent-report', icon: UserX },
      // The SAME page as the one under BO Project Mgmt, listed twice on purpose (CEO 2026-08-27:
      // "same page 2 links") so HR can open it without being granted the whole BO menu. Its home
      // menu is BO — public.tabs.section — and public.tabs.also_in shares it into this one. Both
      // entries must keep the same href, or they stop being the same page.
      { name: 'Saturday Working', href: '/saturday-working', icon: CalendarCheck },
      { name: 'Employee Compliance Report', href: '/employee-compliance-report', icon: UserCheck },
      { name: 'Upcoming Leaves', href: '/leaves', icon: CalendarDays },
      { name: 'Leave Red Flags', href: '/leaves-analytics', icon: ChartPie },
      { name: 'Employees', href: '/employees', icon: Users },
      { name: 'Commitment Agreement', href: '/commitment-agreement', icon: FileCheck },
      { name: 'Zoho People Audit', href: '/zoho-audit', icon: ClipboardCheck },
      { name: 'Last Working Day', href: '/employees?filter=lwd', icon: CalendarDays },
      { name: 'Onboarding Tracker', href: '/onboarding-tracker', icon: UserPlus },
      { name: 'Exit Process', href: '/exit-tracker', icon: UserMinus },
    ],
  },
  {
    name: 'BO Project Mgmt',
    icon: FolderKanban,
    children: [
      { name: 'Client Onboarding', href: '/clients?filter=onboarding', icon: UserPlus },
      { name: 'Screen Integrity Report', href: '/screen-integrity-report', icon: ScanEye },
      { name: 'Saturday Working', href: '/saturday-working', icon: CalendarCheck },
      { name: 'Clients', href: '/clients', icon: Building2 },
      { name: 'SOP Assignments', href: '/sop-assignments', icon: ListChecks },
      { name: 'Team Assignment', href: '/team-assignment', icon: UsersRound },
      { name: 'SOP Reviews', href: '/sop-reviews', icon: BookCheck },
      { name: 'QC Manual Trackers', href: '/qc-manual-trackers', icon: ClipboardList },
      { name: 'Hourly Report', href: '/hourly-report', icon: FileSpreadsheet },
      { name: 'Task Planning', href: '/task-planning', icon: ListTodo },
      { name: 'CUNL Health', href: '/cunl-health', icon: Activity },
      { name: 'Inbox', href: '/inbox', icon: Inbox },
      { name: 'Credentials', href: '/credentials', icon: KeyRound },
      { name: 'Roles & Responsibilities', href: '/roles-and-responsibilities', icon: UserCog },
    ],
  },
  {
    name: 'Technical',
    icon: Server,
    children: [
      { name: 'RDP Servers', href: '/rdp-servers', icon: Server },
      { name: 'AIM Servers', href: '/aim-server-monitoring', icon: Monitor },
      { name: 'GitHub Actions', href: '/github-actions-usage', icon: Github },
    ],
  },
]

interface AppLayoutProps {
  children: ReactNode
}

// Dropdown nav item for desktop
function NavDropdownMenu({ item, isChildAllowed }: { item: NavDropdown; isChildAllowed: (href: string) => boolean }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const location = useLocation()

  // Close on outside click — MUST run before any early return so hook count is stable across renders
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const visibleChildren = item.children.filter(c => isChildAllowed(c.href))
  if (visibleChildren.length === 0) return null

  const isActive = visibleChildren.some(c => location.pathname.startsWith(c.href))

  const handleEnter = () => {
    clearTimeout(timeoutRef.current)
    setOpen(true)
  }
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150)
  }

  return (
    <div ref={ref} className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`group flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors duration-150 whitespace-nowrap ${
          isActive
            ? 'bg-white/10 text-white'
            : 'text-slate-400 hover:bg-white/[0.06] hover:text-slate-200'
        }`}
      >
        <item.icon className={`w-3.5 h-3.5 shrink-0 ${isActive ? 'text-[#7ABC64]' : 'text-slate-500 group-hover:text-slate-300'}`} />
        {item.name}
        <ChevronDown className={`w-3 h-3 shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''} ${isActive ? 'text-slate-300' : 'text-slate-500'}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 pt-1 z-50">
        <div className="w-52 rounded-xl bg-[#1E293B] border border-white/10 shadow-xl shadow-black/20 py-1">
          {visibleChildren.map(child => (
            <NavLink
              key={child.name}
              to={child.href}
              onClick={() => setOpen(false)}
              className={({ isActive: childActive }) =>
                `flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] font-medium transition-colors duration-150 ${
                  childActive
                    ? 'bg-white/10 text-white'
                    : 'text-slate-400 hover:bg-white/[0.06] hover:text-slate-200'
                }`
              }
            >
              {({ isActive: childActive }) => (
                <>
                  <child.icon className={`w-3.5 h-3.5 shrink-0 ${childActive ? 'text-[#7ABC64]' : 'text-slate-500'}`} />
                  {child.name}
                </>
              )}
            </NavLink>
          ))}
        </div>
        </div>
      )}
    </div>
  )
}

export default function AppLayout({ children }: AppLayoutProps) {
  const { auth } = useAuth()
  const { allowedTabs, isLoading: tabsLoading } = useTabAccess()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const userNavigation = [
    { name: 'Your Profile', href: '#' },
    ...(allowedTabs.includes('settings') ? [{ name: 'Settings', href: '/settings', icon: Settings }] : []),
    { name: 'Sign Out', href: '/', action: 'signout' },
  ]

  const userName = auth.user?.user_metadata?.full_name
    || auth.user?.user_metadata?.name
    || auth.user?.email?.split('@')[0]
    || 'User'
  const userEmail = auth.user?.email || ''
  const userInitials = userName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)

  const isItemAllowed = (href: string) => {
    const basePath = href.split('?')[0]
    if (tabsLoading) return true
    const tabName = basePath.replace('/', '')
    return allowedTabs.includes(tabName)
  }

  // Flatten all nav items for mobile menu.
  // DE-DUPLICATED BY href. The desktop nav shows a page once per dropdown, which is the point of
  // listing Saturday Working under both BO Project Mgmt and HR / Admin — but this list has no menu
  // headings, so the same page twice reads as a bug, and `key={item.name}` would be a duplicate
  // React key. First entry wins, so the order here is the order the nav declares.
  const flatNavItems: NavItem[] = navigationItems.flatMap(entry =>
    isDropdown(entry) ? entry.children : [entry]
  )
  const seenHrefs = new Set<string>()
  const filteredFlatItems = flatNavItems.filter((item) => {
    if (!isItemAllowed(item.href) || seenHrefs.has(item.href)) return false
    seenHrefs.add(item.href)
    return true
  })

  return (
    <div className="min-h-screen bg-slate-50">
      {/* The first tab stop on every page in the app, and invisible until it is one.

          Every route puts the whole top nav in front of the page's own first control, on every
          load. This link is the way past it. The measured press counts are deliberately NOT
          repeated here: they are true for one account at one width — this nav renders every
          entry while tab access is still loading, so an account with fewer menus has fewer
          stops — and they are already dated in docs/tasks/skip-to-main-content-qa-checklist.md
          and docs/pipeline-tracker/build-skip-to-main-content.md. A number copied into a comment
          is a number nobody re-measures.

          Not a reorder and not tabindex. Putting <main> before <nav> in the DOM would stop DOM
          order matching visual order (WCAG 1.3.2 / 2.4.3) and send Shift+Tab from the first page
          control to the END of the nav; a positive tabindex lifts one element out of document
          order globally, so every control added afterwards has to join the numbering. Both patch
          one page. This helps every page, including ones not written yet.

          focus:, not focus-visible:. There is no mouse route to this control — it is sr-only
          until focused and it is the first stop — so :focus-visible buys nothing and costs a
          browser-by-browser argument about whether Tab counts.

          Navy on brand green is 7.8:1 (measured 7.83:1 in Chromium, 2026-09-17). White on brand
          green is 2.28:1 and fails, which is why
          the text is #0F172A. z-50 clears the nav's z-40 and stays under the confirm dialog's
          z-[60]. left uses --page-px so the pill lines up with the logo at every width; when
          focused it sits OVER the logo, which is the approved wireframe. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-[var(--page-px)] focus:top-2 focus:z-50 focus:inline-flex focus:min-h-[44px] focus:items-center focus:rounded-lg focus:bg-[#7ABC64] focus:px-4 focus:py-2.5 focus:text-sm focus:font-semibold focus:text-[#0F172A] focus:shadow-lg focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-white"
      >
        Skip to main content
      </a>

      {/* Top Nav Bar — dark navy */}
      <nav
        className="fixed top-0 left-0 right-0 z-40 border-b border-white/[0.06] shadow-[0_1px_8px_rgba(0,0,0,0.15)]"
        style={{ background: 'linear-gradient(180deg, #0F172A, #1E293B)' }}
      >
        <div className="max-w-[1400px] mx-auto px-[var(--page-px)]">
          <div className="flex items-center h-14 gap-4 overflow-visible">
            {/* Logo */}
            <NavLink to="/redirect" className="flex items-center gap-2 shrink-0 mr-2">
              <div className="w-8 h-8 rounded-lg bg-[#7ABC64] flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div className="hidden sm:flex flex-col leading-none">
                <span className="text-white font-bold text-[13px] tracking-tight">PERFECT</span>
                <span className="text-[#7ABC64] font-semibold text-[10px] tracking-[0.08em]">VIRTUAL TEAM</span>
              </div>
            </NavLink>

            {/* Desktop Nav Items */}
            <div className="hidden lg:flex items-center gap-0.5 flex-1 min-w-0 overflow-visible">
              {navigationItems.map((entry) => {
                if (isDropdown(entry)) {
                  return <NavDropdownMenu key={entry.name} item={entry} isChildAllowed={isItemAllowed} />
                }
                const item = entry
                if (!isItemAllowed(item.href)) return null
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      `group flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors duration-150 whitespace-nowrap ${
                        isActive
                          ? 'bg-white/10 text-white'
                          : 'text-slate-400 hover:bg-white/[0.06] hover:text-slate-200'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon className={`w-3.5 h-3.5 shrink-0 ${isActive ? 'text-[#7ABC64]' : 'text-slate-500 group-hover:text-slate-300'}`} />
                        {item.name}
                      </>
                    )}
                  </NavLink>
                )
              })}
            </div>

            {/* Right: Bell + User */}
            <div className="flex items-center gap-1 ml-auto shrink-0">
              <button
                type="button"
                className="relative p-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors"
              >
                <span className="sr-only">View notifications</span>
                <Bell className="w-5 h-5" />
              </button>

              <Menu as="div" className="relative">
                <MenuButton className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-white/[0.06] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7ABC64]">
                  <div className="w-8 h-8 rounded-full bg-[#7ABC64] flex items-center justify-center text-xs font-semibold text-white">
                    {userInitials}
                  </div>
                  <span className="hidden xl:block text-sm font-medium text-slate-300">{userName}</span>
                </MenuButton>

                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-xl bg-white py-1 shadow-lg ring-1 ring-black/5 outline outline-1 outline-slate-200/50 transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <div className="px-4 py-2 border-b border-slate-100">
                    <p className="text-sm font-medium text-slate-900">{userName}</p>
                    <p className="text-xs text-slate-500 truncate">{userEmail}</p>
                  </div>
                  {userNavigation.map((item) => (
                    <MenuItem key={item.name}>
                      {item.action === 'signout' ? (
                        <button
                          onClick={async () => {
                            await supabase.auth.signOut();
                            window.location.href = '/';
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-slate-700 data-[focus]:bg-slate-50 data-[focus]:outline-none hover:bg-slate-50 transition-colors"
                        >
                          {item.name}
                        </button>
                      ) : (
                        <NavLink
                          to={item.href}
                          className="block px-4 py-2 text-sm text-slate-700 data-[focus]:bg-slate-50 data-[focus]:outline-none hover:bg-slate-50 transition-colors"
                        >
                          {item.name}
                        </NavLink>
                      )}
                    </MenuItem>
                  ))}
                </MenuItems>
              </Menu>

              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile dropdown menu — flat list */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-white/[0.06]">
            <div className="max-w-[1400px] mx-auto px-[var(--page-px)] py-2 flex flex-wrap gap-1">
              {filteredFlatItems.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 rounded-lg px-3 py-2.5 text-[13px] font-medium transition-colors duration-150 ${
                      isActive
                        ? 'bg-white/10 text-white'
                        : 'text-slate-400 hover:bg-white/[0.06] hover:text-slate-200'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon className={`w-4 h-4 ${isActive ? 'text-[#7ABC64]' : 'text-slate-500'}`} />
                      {item.name}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Content Area — full width */}
      {/* tabIndex={-1} is what makes the skip link WORK, and leaving it off is the classic way
          to ship a skip link that only scrolls. Chrome and Safari move keyboard focus to a
          fragment target only when that target is focusable; without it the page jumps but the
          next Tab resumes from the nav, which is the walk the link exists to skip. -1 keeps it
          out of the tab order itself, and out of confirmation-dialog.tsx's focusStopsIn(), whose
          filter is tabIndex >= 0.

          scroll-mt-14 is not cosmetic either. The nav above is `fixed h-14`, so it is out of
          flow and covers the top 56px of the viewport; this element's own mt-14 is the empty space
          that keeps it clear while the page is scrolled to the top. A fragment jump aligns THIS
          element's top with the VIEWPORT top, which slides that first 56px straight back under
          the pinned nav — hiding the very control the link just delivered you to. The 14 must
          track the nav's h-14 above: change one, change both.

          focus:outline-none is deliberate: this is a skip TARGET, not a control. The link that
          sent you here carries the visible ring, and the next Tab lands on a real control with
          its own. */}
      <main id="main-content" tabIndex={-1} className="mt-14 scroll-mt-14 bg-slate-50 focus:outline-none">
        <div className="max-w-[1400px] mx-auto px-[var(--page-px)] pt-6 pb-[var(--page-px)]">
          {children}
          <Footer />
        </div>
      </main>
    </div>
  )
}
